import { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

type SearchBarProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  resultCount?: number;
  debounceMs?: number;
};

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  placeholder = 'Search across all columns...',
  disabled = false,
  resultCount,
  debounceMs = 400,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const showCount = value.trim() && resultCount !== undefined;
  
  // Sync local value when external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  // Debounced onChange
  useEffect(() => {
    if (localValue === value) {
      setIsDebouncing(false);
      return;
    }
    
    setIsDebouncing(true);
    const timer = setTimeout(() => {
      onChange(localValue.trim());
      setIsDebouncing(false);
    }, debounceMs);
    
    return () => clearTimeout(timer);
  }, [localValue, value, onChange, debounceMs]);
  
  const handleClear = () => {
    setLocalValue('');
    onChange('');
    setIsDebouncing(false);
  };
  
  return (
    <div className="relative w-full max-w-md">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        {isDebouncing ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        ) : (
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        )}
      </div>
      <input
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`block w-full rounded-md border py-1.5 pl-9 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
          localValue.trim() ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'
        } ${showCount ? 'pr-24' : 'pr-8'}`}
      />
      {showCount && (
        <div className="pointer-events-none absolute inset-y-0 right-8 flex items-center pr-3">
          <span className="text-xs text-gray-500">{resultCount} found</span>
        </div>
      )}
      {localValue && !disabled && (
        <button
          onClick={handleClear}
          className="absolute inset-y-0 right-0 flex items-center pr-3 hover:text-gray-700"
          aria-label="Clear search"
        >
          <XMarkIcon className="h-4 w-4 text-gray-400" />
        </button>
      )}
    </div>
  );
};

